import { useState } from 'react';

/**
 * Rows that hold their order and membership while someone is in the list
 * (spec 150 US4, as the board does since spec 148).
 *
 * While `holding`, the list keeps the rows and the order it last showed —
 * each row still carries its freshest data, and one that has gone keeps its
 * last known form — so nothing moves under a pointer or a focused control.
 * When the hold ends, the list takes the latest order at once.
 */
export function useSteadyRows<T>(
  rows: readonly T[],
  idOf: (row: T) => string,
  holding: boolean,
): readonly T[] {
  const ids = rows.map(idOf);
  const [shown, setShown] = useState<{ ids: string[]; byId: Map<string, T> }>(
    () => ({ ids, byId: new Map(rows.map((row) => [idOf(row), row])) }),
  );

  if (!holding) {
    // Adjusted during render, the documented shape for state derived from a
    // prop: remember what was shown, for the next hold.
    if (!sameIds(shown.ids, ids)) {
      setShown({ ids, byId: new Map(rows.map((row) => [idOf(row), row])) });
    }
    return rows;
  }

  const latest = new Map(rows.map((row) => [idOf(row), row]));
  return shown.ids.flatMap((id) => {
    const row = latest.get(id) ?? shown.byId.get(id);
    return row === undefined ? [] : [row];
  });
}

function sameIds(a: readonly string[], b: readonly string[]): boolean {
  return a.length === b.length && a.every((id, index) => id === b[index]);
}
